import { computed, onUnmounted, ref, type Ref, watch } from 'vue'

const DEFAULT_MIN_GRIP_HEIGHT = 24
const DEFAULT_HIDE_DELAY = 2000

export function useScrollGrip(
  listRef: Ref<HTMLElement | null>,
  contentRef: Ref<HTMLElement | null>,
  options?: {
    minHeight?: number
    hideDelay?: number
    showOnStart?: boolean
  },
) {
  const minHeight = options?.minHeight ?? DEFAULT_MIN_GRIP_HEIGHT
  const hideDelay = options?.hideDelay ?? DEFAULT_HIDE_DELAY

  const visible = ref(options?.showOnStart ?? false)
  const gripHeight = ref(0)
  const gripY = ref(0)
  let hideTimer: number | null = null

  const style = computed(() => ({
    height: `${gripHeight.value}px`,
    transform: `translateY(${gripY.value}px)`,
  }))

  function getViewportHeight() {
    return listRef.value?.getBoundingClientRect().height ?? 0
  }

  function getContentHeight() {
    return contentRef.value?.scrollHeight ?? 1
  }

  function calculateHeight(viewportHeight: number, contentHeight: number) {
    if (contentHeight <= viewportHeight) return 0
    const ratio = viewportHeight / contentHeight
    return Math.max(viewportHeight * ratio, minHeight)
  }

  function showAndScheduleHide() {
    if (gripHeight.value === 0) return
    visible.value = true
    if (hideTimer !== null) {
      clearTimeout(hideTimer)
    }
    hideTimer = window.setTimeout(() => {
      visible.value = false
    }, hideDelay)
  }

  function recalculate() {
    const vh = getViewportHeight()
    const ch = getContentHeight()
    gripHeight.value = calculateHeight(vh, ch)
    if (gripHeight.value === 0) {
      visible.value = false
    }
  }

  function onScroll() {
    const el = listRef.value
    if (!el) return
    const vh = getViewportHeight()
    const ch = getContentHeight()
    const scrollableHeight = ch - el.clientHeight
    const scrollPercentage = scrollableHeight > 0 ? el.scrollTop / scrollableHeight : 0
    gripY.value = scrollPercentage * (vh - gripHeight.value)
    gripHeight.value = calculateHeight(vh, ch)
    showAndScheduleHide()
  }

  const resizeObserver = new ResizeObserver(() => {
    recalculate()
  })

  watch(
    contentRef,
    (el, oldEl) => {
      if (oldEl) resizeObserver.unobserve(oldEl)
      if (el) resizeObserver.observe(el)
    },
    { immediate: true },
  )

  onUnmounted(() => {
    resizeObserver.disconnect()
    if (hideTimer !== null) {
      clearTimeout(hideTimer)
    }
  })

  return {
    visible,
    style,
    onScroll,
    recalculate,
    showAndScheduleHide,
  }
}
